// https://boost.allnetru.dev/api/documentation/partner#/Campaigns
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CampaignEditorBody,
  CampaignEditorResponse,
  CopyPartnerCampaignResponse,
  GetCampaignFiltersResponse,
  GetPartnerCampaignsResponse,
  CampaignSettingsResponse,
  CampaignForecastResponse,
  GroupForecastResponse,
  CampaignGroup,
  CampaignGroupOverridesSchemaResponse,
  CreateGroupBody,
  UpdateGroupBody,
  GetMediaPlanCampaignsResponse,
  GetMediaPlanStatResponse,
  AiPermissionItem,
  AiPermissionUpdateBody,
} from './models';
import { UserLoginResponse } from '../../models-to-replace/auth';
import { FiltersParams } from '../../models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { ORGANIZATION_TYPE, OrganizationRole } from '../../main/organization/organizationType';
const partner_campaigns = `partner_campaigns`;
interface CampaignsFilters {
  page?: number;
  search?: string;
  statuses?: string[];
  landings?: string[];
  events?: string[];
  goals?: string[];
}
export const partnerCampaigns = createApi({
  reducerPath: partner_campaigns,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_campaigns],
  endpoints: (build) => ({
    getPartnerCampaigns: build.query<
      GetPartnerCampaignsResponse,
      { filter?: Partial<CampaignsFilters>; role?: OrganizationRole }
    >({
      query: ({ filter, role }) => {
        const params = new URLSearchParams();
        if (filter?.search) {
          params.append('filters[search]', filter?.search);
        }
        if (filter?.events) {
          filter.events.forEach((id) => params.append('filters[events][]', id));
        }
        if (filter?.page) {
          params.append('page', filter.page.toString());
        }
        if (filter?.landings) {
          filter.landings.forEach((id) => params.append('filters[landings][]', id.toString()));
        }
        if (filter?.statuses) {
          filter.statuses.forEach((id) => params.append('filters[statuses][]', id.toString()));
        }

        const queryString = params.toString();

        if (role === ORGANIZATION_TYPE.organizer) {
          if (!filter?.events?.[0]) {
            console.error('Event ID is required');
            return '';
          }
          return {
            url: `/organizer/events/${filter.events?.[0]}/campaigns${queryString ? '?' + queryString : ''}`,
            headers: {
              Accept: 'application/json',
            },
          };
        }
        return {
          url: `/partner/campaigns${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
    getCampaignFilterOptions: build.query<
      GetCampaignFiltersResponse,
      {
        role?: OrganizationRole;
        filters?: FiltersParams;
        search?: string;
        page?: number;
      }
    >({
      query: ({ role, filters, search, page }) => {
        const params = new URLSearchParams();

        const relationsQuery = [
          getQueryStringFromParams(filters?.events, 'events', 'relations'),
          getQueryStringFromParams(filters?.landings, 'landings', 'relations'),
          getQueryStringFromParams(filters?.partners, 'partners', 'relations'),
          getQueryStringFromParams(filters?.campaigns, 'campaigns', 'relations'),
        ]
          .filter(Boolean)
          .join('&');

        if (relationsQuery) {
          new URLSearchParams(relationsQuery).forEach((value, key) => {
            params.append(key, value);
          });
        }

        const pageParam = page ?? filters?.page;
        if (typeof pageParam === 'number') {
          params.append('page', pageParam.toString());
        }

        if (search) {
          params.append('search', search);
        }

        const normalizedRole = role || 'partner';
        const queryString = params.toString();

        return {
          url: `/${normalizedRole}/campaigns/filter${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
    postCreateCampaignByEventId: build.mutation<CampaignEditorResponse, CampaignEditorBody>({
      query: (body) => {
        return {
          method: 'POST',
          url: `/partner/campaigns`,
          headers: {
            Accept: 'application/json',
          },
          body,
        };
      },
    }),
    getCampaignEditorDataById: build.query<CampaignEditorResponse, { campaignId: string }>({
      query: ({ campaignId }) => ({
        url: `/partner/campaigns/${campaignId}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { campaignId }) => [
        { type: partner_campaigns, id: campaignId },
      ],
    }),
    patchCampaignDataById: build.mutation<CampaignEditorResponse, CampaignEditorBody>({
      query: ({ campaign_id, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/partner/campaigns/${campaign_id!}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: partner_campaigns, id: 'LIST' }],
      async onQueryStarted({ campaign_id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            partnerCampaigns.util.updateQueryData(
              'getCampaignEditorDataById',
              { campaignId: campaign_id! },
              () => data,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    copyPartnerCampaign: build.mutation<
      CopyPartnerCampaignResponse,
      { campaignId: string; title?: string; linkWithSource?: boolean }
    >({
      query: ({ campaignId, title, linkWithSource = false }) => {
        const body: Record<string, unknown> = {
          link_with_source: linkWithSource,
        };
        if (title && title.trim().length) {
          body.title = title.trim();
        }

        return {
          method: 'POST',
          url: `/partner/campaigns/${campaignId}/copy`,
          headers: {
            Accept: 'application/json',
          },
          body,
        };
      },
      invalidatesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
    getCampaignSettings: build.query<
      CampaignSettingsResponse,
      { eventId?: string; campaignId?: string; data: CampaignEditorBody }
    >({
      query: ({ eventId, campaignId, data }) => {
        const base = `/partner/campaigns/settings${campaignId ? `/${campaignId}` : ''}`;
        const qs = !campaignId && eventId ? `?event_id=${eventId}` : '';
        return {
          url: `${base}${qs}`,
          method: 'POST',
          body: data,
          headers: {
            Accept: 'application/json',
          },
        };
      },
    }),
    getCampaignForecast: build.query<
      CampaignForecastResponse,
      { campaignId: string; data: CampaignEditorBody }
    >({
      query: ({ campaignId, data }) => ({
        method: 'POST',
        url: `/partner/campaigns/${campaignId}/forecast`,
        body: data,
        headers: { Accept: 'application/json' },
      }),
    }),
    getGroupForecast: build.query<
      GroupForecastResponse,
      { campaignId: string; groupId: number; data: UpdateGroupBody }
    >({
      query: ({ campaignId, groupId, data }) => ({
        method: 'POST',
        url: `/partner/campaigns/${campaignId}/groups/${groupId}/forecast`,
        body: data,
        headers: { Accept: 'application/json' },
      }),
    }),
    // getPartnerCampaignStatuses: build.query<GetPartnerCampaignStatusesResponse, void>({
    //     query: () => ({
    //         url: `/partner/campaign/statuses`,
    //         headers: {
    //             Accept: 'application/json',
    //         },
    //     }),
    //     providesTags: [partner_campaigns],
    // }),

    patchPartnerCampaignsStatusById: build.mutation<
      CampaignEditorResponse,
      {
        campaignIds: string[];
        status: number;
        transition: number;
        filters?: CampaignsFilters;
      }
    >({
      query: ({ campaignIds, status, transition, filters }) => {
        return {
          method: 'PATCH',
          url: `/partner/campaigns/status/${status}`,
          headers: {
            Accept: 'application/json',
          },
          body: {
            transition,
            filters: {
              ids: campaignIds,
              filters: {
                events: filters?.events,
                landings: filters?.landings,
                goals: filters?.goals,
                statuses: filters?.statuses,
                search: filters?.search,
              },
            },
          },
        };
      },
      invalidatesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
    // Campaign Groups CRUD
    postCreateGroup: build.mutation<
      { data: CampaignGroup },
      { campaignId: string; body: CreateGroupBody }
    >({
      query: ({ campaignId, body }) => ({
        method: 'POST',
        url: `/partner/campaigns/${campaignId}/groups`,
        headers: { Accept: 'application/json' },
        body,
      }),
      async onQueryStarted({ campaignId }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          dispatch(
            partnerCampaigns.util.updateQueryData(
              'getCampaignEditorDataById',
              { campaignId },
              (draft) => {
                if (draft.data.groups) {
                  draft.data.groups.push(result.data);
                } else {
                  draft.data.groups = [result.data];
                }
              },
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    patchUpdateGroup: build.mutation<
      { data: CampaignGroup },
      { campaignId: string; groupId: number; body: UpdateGroupBody }
    >({
      query: ({ campaignId, groupId, body }) => ({
        method: 'PATCH',
        url: `/partner/campaigns/${campaignId}/groups/${groupId}`,
        headers: { Accept: 'application/json' },
        body,
      }),
      // nothing changed server-side on a failed save — invalidating would
      // refetch the overrides schema and wipe the user's draft mid-correction
      invalidatesTags: (_result, error, { groupId }) =>
        error
          ? []
          : [
              { type: partner_campaigns, id: 'LIST' },
              { type: partner_campaigns, id: `OVERRIDES_${groupId}` },
            ],
      async onQueryStarted({ campaignId, groupId }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          dispatch(
            partnerCampaigns.util.updateQueryData(
              'getCampaignEditorDataById',
              { campaignId },
              (draft) => {
                const idx = draft.data.groups?.findIndex((g) => g.id === groupId);
                if (idx !== undefined && idx !== -1 && draft.data.groups) {
                  draft.data.groups[idx] = result.data;
                }
              },
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    getGroupOverridesSchema: build.query<
      CampaignGroupOverridesSchemaResponse,
      { campaignId: string; groupId: number }
    >({
      query: ({ campaignId, groupId }) => ({
        url: `/partner/campaigns/${campaignId}/groups/${groupId}/overrides-schema`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { groupId }) => [
        { type: partner_campaigns, id: `OVERRIDES_${groupId}` },
      ],
    }),
    patchGroupMassStatus: build.mutation<
      { data: CampaignGroup[] },
      {
        campaignId: string;
        status: number;
        transition: number;
        filters: {
          ids: Array<string | number>;
        };
      }
    >({
      query: ({ campaignId, status, transition, filters }) => ({
        method: 'PATCH',
        url: `/partner/campaigns/${campaignId}/groups/status/${status}`,
        headers: { Accept: 'application/json' },
        body: {
          transition,
          status,
          filters: {
            ids: filters?.ids,
          },
        },
      }),
      invalidatesTags: [{ type: partner_campaigns, id: 'LIST' }],
      async onQueryStarted({ campaignId }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          dispatch(
            partnerCampaigns.util.updateQueryData(
              'getCampaignEditorDataById',
              { campaignId },
              (draft) => {
                if (draft.data.groups) {
                  for (const updatedGroup of result.data) {
                    const idx = draft.data.groups.findIndex((g) => g.id === updatedGroup.id);
                    if (idx !== -1) {
                      draft.data.groups[idx] = updatedGroup;
                    }
                  }
                }
              },
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    deleteGroup: build.mutation<CampaignEditorResponse, { campaignId: string; groupId: number }>({
      query: ({ campaignId, groupId }) => ({
        method: 'DELETE',
        url: `/partner/campaigns/${campaignId}/groups/${groupId}`,
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted({ campaignId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            partnerCampaigns.util.updateQueryData(
              'getCampaignEditorDataById',
              { campaignId },
              () => data,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    // AI Permissions
    patchCampaignAiPermission: build.mutation<
      { data: AiPermissionItem },
      { campaignId: string; body: AiPermissionUpdateBody }
    >({
      query: ({ campaignId, body }) => ({
        method: 'PATCH',
        url: `/partner/campaigns/${campaignId}/ai-permissions`,
        headers: { Accept: 'application/json' },
        body,
      }),
    }),
    postUpdateCompanyData: build.mutation<UserLoginResponse, { name: string; description: string }>(
      {
        query: (body) => ({
          body,
          method: 'POST',
          url: `/organizer/onboarding`,
          headers: {
            Accept: 'application/json',
          },
        }),
      },
    ),
    getPartnerMediaPlanCampaigns: build.query<
      GetMediaPlanCampaignsResponse,
      {
        page?: number;
        search?: string;
        statuses?: string[];
        goals?: string[];
        events?: string[];
        sortField?: string;
        sortDirection?: string;
      }
    >({
      query: ({ page, search, statuses, goals, events, sortField, sortDirection }) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (search) params.append('filters[search]', search);
        if (statuses) statuses.forEach((id) => params.append('filters[statuses][]', id.toString()));
        if (goals) goals.forEach((id) => params.append('filters[goals][]', id.toString()));
        if (events) events.forEach((id) => params.append('filters[events][]', id));
        if (sortField && sortDirection) {
          params.append('sort[0][field]', sortField);
          params.append('sort[0][direction]', sortDirection);
        }
        const queryString = params.toString();
        return {
          url: `/partner/campaigns/media-plan${queryString ? '?' + queryString : ''}`,
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
    getPartnerMediaPlanStat: build.query<
      GetMediaPlanStatResponse,
      { search?: string; statuses?: string[]; goals?: string[]; events?: string[] }
    >({
      query: ({ search, statuses, goals, events }) => {
        const params = new URLSearchParams();
        if (search) params.append('filters[search]', search);
        if (statuses) statuses.forEach((id) => params.append('filters[statuses][]', id.toString()));
        if (goals) goals.forEach((id) => params.append('filters[goals][]', id.toString()));
        if (events) events.forEach((id) => params.append('filters[events][]', id));
        const queryString = params.toString();
        return {
          url: `/partner/campaigns/media-plan/stat${queryString ? '?' + queryString : ''}`,
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: partner_campaigns, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPartnerCampaignsQuery,
  useGetCampaignFilterOptionsQuery,
  usePostCreateCampaignByEventIdMutation,
  useGetCampaignEditorDataByIdQuery,
  useLazyGetCampaignEditorDataByIdQuery,
  usePatchCampaignDataByIdMutation,
  useCopyPartnerCampaignMutation,
  usePatchPartnerCampaignsStatusByIdMutation,
  useLazyGetCampaignSettingsQuery,
  useLazyGetCampaignForecastQuery,
  useGetGroupForecastQuery,
  useLazyGetGroupForecastQuery,
  useLazyGetGroupOverridesSchemaQuery,
  useGetGroupOverridesSchemaQuery,
  usePostUpdateCompanyDataMutation,
  usePatchCampaignAiPermissionMutation,
  usePostCreateGroupMutation,
  usePatchUpdateGroupMutation,
  usePatchGroupMassStatusMutation,
  useDeleteGroupMutation,
  useGetPartnerMediaPlanCampaignsQuery,
  useGetPartnerMediaPlanStatQuery,
} = partnerCampaigns;
